import api from "@api/api.js";

/**
 * Counts gallery detail views.
 *
 * The SPA renders #/gallery/:id from data it already has, so opening a gallery makes
 * no backend request the server could count. Image requests are not a usable
 * stand-in: one view fires dozens, the home grid fires them for galleries
 * nobody opened, and they're served `immutable` so a returning visitor fires
 * none. Hence an explicit ping — exactly one per gallery view.
 *
 * Deliberately anonymous and cookieless: the visit id lives in this module for
 * the lifetime of one view and is never stored, so nothing links two visits to
 * the same person. That keeps the cookie banner's "no tracking cookies" and the
 * privacy page's "no analytics suite" honest.
 */
class VisitTracker {
  constructor() {
    this.visitId = null;
    this.startedAt = 0;
    // Bumped by every start() and finish(). A start() whose request is still in
    // flight compares against it to find out whether its visit is already over.
    this.generation = 0;
    this.watchPageHide();
  }

  /**
   * Begin timing a gallery view. Any previous view is closed out first.
   *
   * Every view is reported, the admin's included — the backend decides which
   * audience a view belongs to and reports the two separately. An earlier
   * version skipped the request entirely when logged in as admin, which meant
   * the only person who can read the numbers could never see one appear.
   */
  async start(galleryId) {
    this.finish();

    const token = ++this.generation;
    const startedAt = Date.now();
    try {
      const res = await api.post("/api/visits", { galleryId });
      // The visitor may have left, or opened a different gallery, while this
      // was in flight — a newer generation means this visit is already over and
      // adopting its id would misattribute the next one.
      if (token !== this.generation) return;
      this.visitId = res?.visitId || null;
      this.startedAt = startedAt;
    } catch {
      // Never let a failed counter break the gallery the visitor came to see.
    }
  }

  /**
   * Report dwell time and stop timing. Uses sendBeacon so the request survives
   * the page being closed — a normal fetch is cancelled on unload, which would
   * lose the duration for every visit that ends by closing the tab.
   */
  finish() {
    const { visitId, startedAt } = this;
    this.visitId = null;
    this.startedAt = 0;
    this.generation++;
    if (!visitId) return;

    const payload = { durationMs: Date.now() - startedAt };
    const url = `/api/visits/${encodeURIComponent(visitId)}/finish`;

    if (navigator.sendBeacon) {
      // The Blob's type becomes the Content-Type; express.json() needs it.
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
    } else {
      api.post(url, payload).catch(() => {});
    }
  }

  /**
   * `pagehide` rather than `beforeunload`: it fires on mobile Safari's
   * back-forward cache path, which `beforeunload` does not, and that is where a
   * large share of real visits end.
   */
  watchPageHide() {
    if (typeof window === "undefined") return;
    window.addEventListener("pagehide", () => this.finish());
  }
}

export default new VisitTracker();
